import { parseUrlQuery, stringifyUrlQuery } from 'dew-qs'
import { WebsiteRoute } from 'dew-types'
import { compile, PathFunction } from 'path-to-regexp'
import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'

import {
  LinkValue,
  MatchedRoute,
  RouterLocation,
  RouterNavigator,
  StringOrStringArray,
  UrlParams,
} from './types'

export interface LocationContextValue {
  routes: WebsiteRoute[]
  matchedRoutes: MatchedRoute[]
  location: RouterLocation
  navigator: RouterNavigator
  query: Record<string, StringOrStringArray>
  pathFnMap: Record<string, PathFunction<UrlParams>>
}

export const LocationContext = createContext<LocationContextValue | undefined>(undefined)

export interface RouterProps {
  routes: WebsiteRoute[]
  matchedRoutes: MatchedRoute[]
  children: ReactNode
  location: RouterLocation
  navigator: RouterNavigator
}

export function makePathFnMap(_routes: WebsiteRoute[]) {
  const routes = [..._routes]
  const result: Record<string, PathFunction<UrlParams>> = {}
  while (routes.length > 0) {
    const r = routes.shift()
    if (!r) {
      break
    }
    const { children, name, lang, pattern } = r
    result[`${name}:${lang}`] = compile(pattern)
    if (Array.isArray(children)) {
      routes.push(
        ...children.map((child) => ({
          ...child,
          pattern: [pattern, child.pattern.replace(/^\//, '')].join('/'),
        })),
      )
    }
  }
  return result
}

export function Router({
  location,
  routes,
  matchedRoutes,
  navigator,
  children,
}: RouterProps) {
  const pathFnMap: Record<string, PathFunction<UrlParams>> = useMemo(() => {
    return makePathFnMap(routes)
  }, [routes])

  return (
    <LocationContext.Provider
      value={{
        location,
        navigator,
        routes,
        matchedRoutes,
        pathFnMap,
        query: parseUrlQuery(location.search),
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export function useRouter() {
  const context = useContext(LocationContext)
  if (!context) {
    throw new Error('useRouter must be use within StaticRouter or BrowserRouter')
  }
  const { pathFnMap } = context

  const parseLink = useCallback(
    ({ name, lang, params, query }: LinkValue) => {
      const key = `${name}:${lang}`
      const fn = pathFnMap[key]
      if (!fn) {
        console.warn(`Cannot find route with key = ${key}`)
        return null
      }
      return [fn(params), query && stringifyUrlQuery(query)].filter(Boolean).join('?')
    },
    [pathFnMap],
  )

  return {
    routes: context.routes,
    matchedRoutes: context.matchedRoutes,
    location: context.location,
    query: context.query,
    navigator: context.navigator,
    parseLink,
    pathFnMap,
  }
}
